import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchAll } from '../lib/fetchall'
import { useRoster } from '../lib/roster'
import { fullName, formatDate } from '../lib/format'
import { deleteTask, toggleTaskDone } from '../lib/tasks'
import { TASK_SORTS, isOverdue, sortTasks, type TaskSortKey } from '../lib/tasksort'
import { emptyView } from '../lib/views'
import type { Agent, Task } from '../lib/types'

/**
 * Every task, across every agent — the walkthrough-flagged 2026-08-21
 * companion to the per-agent checklist on `AgentPanel.tsx`. Same table,
 * same `toggleTaskDone`/`deleteTask`, different view: "what do I owe
 * anyone" rather than "what do I owe this one person".
 *
 * No create-a-task form here, deliberately. A task is always tied to one
 * agent (`tasks.agent_id` is `not null` — see the migration), and the
 * natural place to write one is the record of the agent it's about, which
 * is already where Notes and Tags live. Picking an agent out of 785 from a
 * blank tab is a worse version of the same action.
 */
export default function Tasks() {
  const r = useRoster()
  const navigate = useNavigate()
  const [tasks, setTasks] = useState<Task[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sortKey, setSortKey] = useState<TaskSortKey>('due')
  const [showDone, setShowDone] = useState(false)
  const [busy, setBusy] = useState<Set<number>>(new Set())

  const load = useCallback(async () => {
    // `fetchAll`, not a plain `.select('*')` — this is every task across
    // every agent, not one agent's own handful, and PostgREST silently caps
    // an unpaginated read at 1000 rows. See `lib/fetchall.ts`. Ordered by
    // `id` only for a sound total order to page against; `sortTasks` below
    // re-sorts for display regardless, so the order fetchAll returns in
    // never reaches the screen.
    const { rows, error } = await fetchAll<Task>('tasks', '*', ['id'])
    if (error) setError(error)
    else setTasks(rows)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (error) return <div className="empty">Could not load tasks: {error}</div>
  if (!tasks || !r.agents) return <div className="empty">Loading…</div>

  const byId = new Map(r.agents.map((a) => [a.rezen_id, a]))
  const nameOf = (agentId: string) => (byId.get(agentId) ? fullName(byId.get(agentId) as Agent) : 'Unknown agent')

  const open = tasks.filter((t) => !t.done)
  const done = tasks.filter((t) => t.done)
  const shown = sortTasks(showDone ? tasks : open, sortKey, nameOf)

  async function toggle(t: Task) {
    setBusy((prev) => new Set(prev).add(t.id))
    try {
      const { task, error } = await toggleTaskDone(t)
      if (error) {
        setError(error)
        return
      }
      if (task) setTasks((prev) => (prev ?? []).map((x) => (x.id === task.id ? task : x)))
    } finally {
      setBusy((prev) => {
        if (!prev.has(t.id)) return prev
        const next = new Set(prev)
        next.delete(t.id)
        return next
      })
    }
  }

  async function remove(t: Task) {
    setBusy((prev) => new Set(prev).add(t.id))
    try {
      const error = await deleteTask(t.id)
      if (error) {
        setError(error)
        return
      }
      setTasks((prev) => (prev ?? []).filter((x) => x.id !== t.id))
    } finally {
      setBusy((prev) => {
        if (!prev.has(t.id)) return prev
        const next = new Set(prev)
        next.delete(t.id)
        return next
      })
    }
  }

  /**
   * The "show me" pattern `Gaps.tsx` uses for a whole category, aimed at one
   * agent and carried the rest of the way: build a view that searches for
   * them specifically (so they're guaranteed to be in the roster's `rows`
   * regardless of whatever filters are on screen already), then land on `/`
   * with `?agent=<rezen_id>` so `Workspace.tsx` opens their record directly
   * instead of leaving her to click the one row it narrowed down to.
   *
   * The agent's email is the search term, not their name, because names
   * collide and emails essentially never do; falls back to name only for
   * the rare agent with no email on file. `include_departed: true` because
   * a task can outlive the agent leaving — an offboarding checklist item is
   * exactly the case where hiding departed agents from the result would
   * make this show nobody.
   */
  function openAgent(agentId: string) {
    const a = byId.get(agentId)
    if (!a) return
    const view = { ...emptyView(fullName(a)), search: a.email ?? fullName(a), include_departed: true }
    r.applyView(view)
    navigate(`/?agent=${encodeURIComponent(agentId)}`)
  }

  return (
    <div className="gaps">
      <h2>Tasks</h2>
      <p className="hint">
        Checklist items across every agent. Checking this tab is the only reminder there is —
        nothing here sends an email or a notification.
      </p>

      <div className="toolbar" style={{ padding: '10px 0' }}>
        <span className="cnt">
          {open.length} open{done.length > 0 ? ` · ${done.length} done` : ''}
        </span>
        <label className="f" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <span className="hint">Sort by</span>
          <select
            className="areasel-d"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as TaskSortKey)}
          >
            {TASK_SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        {done.length > 0 && (
          <button className="btn ghost sm" onClick={() => setShowDone((v) => !v)}>
            {showDone ? `Hide the ${done.length} done` : `Show ${done.length} done`}
          </button>
        )}
      </div>

      {tasks.length === 0 && (
        <div className="hint">
          Nothing yet. Add one from any agent's own record — there is a Tasks section there, like
          Notes.
        </div>
      )}

      {tasks.length > 0 && shown.length === 0 && (
        <div className="hint">
          Nothing open. {done.length} {done.length === 1 ? 'task has' : 'tasks have'} been checked
          off — press <strong>Show {done.length} done</strong> to read them.
        </div>
      )}

      {shown.map((t) => (
        <div className={`task${t.done ? ' done' : ''}`} key={t.id}>
          <label className="chk">
            <input
              type="checkbox"
              checked={t.done}
              disabled={busy.has(t.id)}
              onChange={() => void toggle(t)}
              aria-label={t.done ? `Mark "${t.body}" not done` : `Mark "${t.body}" done`}
            />
          </label>
          <button className="btn ghost sm" onClick={() => openAgent(t.agent_id)} style={{ minWidth: 140, textAlign: 'left' }}>
            {nameOf(t.agent_id)}
          </button>
          <span className="task-body">{t.body}</span>
          {t.due_date && (
            <span className={isOverdue(t) ? 'task-overdue' : 'hint'}>
              {isOverdue(t) ? 'Overdue · ' : ''}
              {formatDate(t.due_date)}
            </span>
          )}
          <button
            className="btn ghost sm"
            onClick={() => void remove(t)}
            disabled={busy.has(t.id)}
            aria-label={`Delete task "${t.body}" for ${nameOf(t.agent_id)}`}
          >
            Delete
          </button>
        </div>
      ))}
    </div>
  )
}
